import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy,
  Compass,
  Users,
  Heart,
  Star,
  MapPin,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { getAboutContent, getLocalAboutContent } from "../api";
import { HERO_IMAGE } from "../constants";
import SEO from "../components/SEO";
import { buildOrganizationSchema, buildBreadcrumbSchema } from "../utils/seoSchemas";

const ICON_MAP = {
  Trophy: Trophy,
  Compass: Compass,
  Users: Users,
  Heart: Heart,
  Star: Star,
  ShieldCheck: ShieldCheck,
};

const AboutUs = () => {
  const [content, setContent] = useState(() => getLocalAboutContent());

  useEffect(() => {
    getAboutContent()
      .then((data) => {
        if (data) setContent(data);
      })
      .catch(() => {});
  }, []);

  const stats = content?.stats || [
    { number: "4,000+", label: "Courses Listed", description: "Verified courses nationwide" },
    { number: "50", label: "States Covered", description: "Every state and major metro area" },
    { number: "250K+", label: "Monthly Putters", description: "Players discovering new courses" },
    { number: "4.8★", label: "Average Rating", description: "Authentic visitor reviews" },
  ];

  const values = content?.values || [
    {
      icon: "Trophy",
      title: "Verified & Accurate Listings",
      description: "Every course profile includes accurate operating hours, direct phone numbers, admission rates, and genuine visitor reviews.",
    },
    {
      icon: "Compass",
      title: "Comprehensive Geography",
      description: "From coastal resort greens in Florida and California to indoor climate-controlled courses in the Midwest, we cover every corner of the nation.",
    },
    {
      icon: "Users",
      title: "Community-Driven Insights",
      description: "Real feedback from players helps you pick the right venue for kids' birthday parties, romantic date nights, or competitive tournaments.",
    },
    {
      icon: "Heart",
      title: "Passionate About Putt-Putt",
      description: "We are enthusiasts ourselves on a mission to celebrate the timeless joy, creativity, and friendly competition of mini golf.",
    },
  ];

  const storyParagraphs = content?.storyParagraphs || [
    "Mini Golf USA was born out of a simple passion: making it easy for families, couples, travelers, and mini golf enthusiasts to find incredible miniature golf experiences wherever they are in the country.",
    "From vintage 1950s obstacle courses with spinning windmills to multi-million dollar adventure courses featuring pirate ships, erupting volcanoes, and glow-in-the-dark blacklight rooms, miniature golf remains one of the most accessible and joyful pastimes in American culture.",
    "Our team continuously audits and verifies course details, operating hours, ticket pricing, and visitor reviews. We believe everyone deserves a fun, affordable day out on the greens.",
  ];

  const aboutSchemas = [
    buildOrganizationSchema(),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "About Us", url: "/about" },
    ]),
  ];

  return (
    <div className="bg-[#0d0e12] text-gray-100 min-h-screen font-sans" data-testid="about-us-page">
      <SEO
        title="About Us | Mini Golf USA Directory"
        description="Learn about Mini Golf USA, the definitive nationwide directory celebrating mini golf, putt putt, and glow golf courses."
        canonicalUrl="https://minigolfalabama.com/about"
        ogType="website"
        schema={aboutSchemas}
      />
      {/* ================= HERO SECTION ================= */}
      <section className="relative min-h-[440px] flex items-center bg-[#111217] border-b border-gray-800 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Mini golf course overview"
            className="w-full h-full object-cover opacity-20 filter saturate-150"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0e12] via-[#0d0e12]/90 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0e12] via-transparent to-black/60" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="text-gray-600">/</span>
            <span className="text-gray-200 font-medium">About Us</span>
          </nav>

          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-white border border-white/15 backdrop-blur-md mb-5">
              <Sparkles className="w-3.5 h-3.5 text-[#e5243b]" />
              {content?.heroBadge || "ABOUT MINIGOLF USA"}
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1]">
              {content?.heroTitle || "Connecting America with the Joy of Miniature Golf"}
            </h1>

            <p className="mt-5 text-base sm:text-lg text-gray-300 leading-relaxed max-w-2xl font-normal">
              {content?.heroSubtitle ||
                "Your premier directory for discovering over 4,000 top-rated mini golf courses, putt-putt greens, and family entertainment destinations across all 50 states."}
            </p>
          </div>
        </div>
      </section>

      {/* ================= STATS COUNTER BANNER ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="bg-[#181a22] border border-gray-800 rounded-2xl p-5 sm:p-6 text-center shadow-xl hover:border-gray-700 transition-all"
            >
              <div className="text-3xl sm:text-4xl font-extrabold text-[#e5243b] tracking-tight">
                {stat.number}
              </div>
              <div className="text-sm font-bold text-white mt-1">{stat.label}</div>
              {stat.description && (
                <div className="text-xs text-gray-400 mt-1">{stat.description}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ================= STORY & MISSION SECTION ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#e5243b]">
              <span className="w-2 h-2 rounded-full bg-[#e5243b]"></span>
              Who We Are
            </div>

            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight leading-snug">
              {content?.storyTitle || "Our Mission & Story"}
            </h2>

            <div className="space-y-4 text-base text-gray-300 leading-relaxed">
              {storyParagraphs.map((para, i) => (
                <p key={i} className="text-gray-300">
                  {para}
                </p>
              ))}
            </div>

            <div className="pt-4 flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> 100% Free for Players
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Weekly Data Refreshes
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> 50 States Covered
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-gray-800 shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1628766424655-f6cc8990fd1b?auto=format&fit=crop&q=80&w=800"
                alt="Mini golf glow in the dark"
                className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent flex flex-col justify-end p-8">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-1">
                  Community Focused
                </span>
                <h3 className="text-xl font-bold text-white leading-tight">
                  Over 4,000 Courses Mapped Nationwide
                </h3>
                <p className="text-xs text-gray-300 mt-2">
                  From oceanfront putting in Myrtle Beach to glow-in-the-dark courses in Las Vegas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= VALUES / WHAT SETS US APART ================= */}
      <section className="bg-[#121319] border-y border-gray-800/80 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#e5243b] mb-3">
              <span className="w-2 h-2 rounded-full bg-[#e5243b]"></span>
              Why Choose Our Directory
            </div>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              {content?.valuesTitle || "What Sets Us Apart"}
            </h2>
            <p className="text-sm text-gray-400 mt-3">
              We focus on building the most useful, accurate, and player-friendly miniature golf platform on the web.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((val, idx) => {
              const IconComponent = ICON_MAP[val.icon] || Trophy;
              return (
                <div
                  key={idx}
                  className="bg-[#181a22] border border-gray-800 rounded-2xl p-6 hover:border-[#e5243b]/60 transition-all duration-300 flex flex-col group shadow-lg"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#e5243b]/10 group-hover:bg-[#e5243b] flex items-center justify-center mb-5 text-[#e5243b] group-hover:text-white transition-colors">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-white group-hover:text-white transition-colors">
                    {val.title}
                  </h3>
                  <p className="text-sm text-gray-400 mt-2.5 leading-relaxed flex-1 font-normal">
                    {val.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= CALL TO ACTION ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-[#181a22] via-[#202330] to-[#181a22] border border-gray-800 rounded-3xl p-8 sm:p-12 shadow-2xl text-center flex flex-col items-center">
          <div className="w-16 h-16 rounded-2xl bg-[#e5243b] flex items-center justify-center mb-6 shadow-lg shadow-red-900/50">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            {content?.ctaTitle || "Ready to Plan Your Next Round?"}
          </h2>
          <p className="text-sm sm:text-base text-gray-300 max-w-xl mt-3 leading-relaxed">
            {content?.ctaSubtitle ||
              "Browse courses by state, explore top-rated destinations, or read our expert guides."}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link
              to="/state/alabama"
              className="px-8 py-3.5 rounded-xl bg-[#e5243b] hover:bg-[#cf1c31] text-white text-sm font-bold shadow-lg shadow-red-900/30 transition-all flex items-center justify-center gap-2"
            >
              <span>Browse Alabama Courses</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/top-rated"
              className="px-8 py-3.5 rounded-xl bg-[#252833] hover:bg-gray-700 text-white text-sm font-bold border border-gray-700 transition-all flex items-center justify-center gap-2"
            >
              <span>View Top Rated Courses</span>
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3.5 rounded-xl bg-transparent hover:bg-white/5 text-gray-300 hover:text-white text-sm font-bold border border-gray-800 transition-all flex items-center justify-center gap-2"
            >
              <span>Contact Our Team</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
